import { isValidDate } from "@/helpers/date.helper";
import { Gender } from "@/services/auth/types/auth.types";
import { isValidEmail, fullNameRegex, fullPhoneNumberRegex } from "@/utils/regex";
import { realityInfo } from "@/utils/realityInfo";
import { isAfter } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithCustomToken,
  signInWithPhoneNumber,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "@/firebase.config";
import {
  firebaseSignup,
  sendWhatsappCode,
  signUpLite,
  validateWhatsappCode,
} from "@/services/auth";
import { useLoader } from "@/context/LoaderContext";
import {
  getEmailAvailability,
  getPhoneNumberAvailability,
  updateUserInfo,
  setReseleCodeAvailability,
} from "@/services/users";
import type { UserInfo } from "@/services/users/types/user.types";
import { useCountdown } from "@/hooks/useCountdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useResaleCode } from "@/hooks/useResaleCode";
import { getBlacklist } from "@/services/blacklist";
import { queryClient } from "@/services/react-query";
import { resetTokenRefresh } from "@/services/api";
import {
  clearPendingPhoneValidatedSyncFlag,
  setPendingPhoneValidatedSyncFlag,
} from "@/utils/phoneValidatedSync";
import {
  getFirebasePhoneAuthToastMessage,
  isFirebasePhoneAuthCaptchaError,
} from "@/utils/firebasePhoneAuthErrors";

interface CustomWindow extends Window {
  confirmationResult?: ConfirmationResult | null;
  recaptchaVerifier?: RecaptchaVerifier;
  recaptchaWidgetId?: number;
}

declare const window: CustomWindow;

// Comes from Google reCAPTCHA V3 script included in the HTML file
declare const grecaptcha: any;

/** Passed when navigating from login (no account) to skip the phone intro dialog. */
export type SignUpLocationState = {
  skipPhoneIntro?: boolean;
  prefilledPhone?: string;
};

export type SignInLocationState = {
  prefilledPhone?: string;
};

export type ResaleCodeAnswer = "yes" | "no" | null;

/**
 * Pause after `firebase-signup` succeeds and before `signInWithPhoneNumber`.
 * Override with `VITE_MS_AFTER_FIREBASE_SIGNUP_BEFORE_SMS` (ms). Default 2000; increase in prod if needed.
 */
const MS_AFTER_FIREBASE_SIGNUP_BEFORE_SMS = (() => {
  const n = Number(import.meta.env.VITE_MS_AFTER_FIREBASE_SIGNUP_BEFORE_SMS);
  return Number.isFinite(n) && n >= 0 ? n : 2000;
})();

/** Only for transient errors (network, etc.). Never for reCAPTCHA — user resolves UI and retries via "Reenviar". */
const MAX_SMS_TRANSIENT_RETRIES = 3;

export const useSignup = () => {
  const loader = useLoader();
  const navigate = useNavigate();
  const { realityId: resaleCodeFromRoute } = useParams();
  const location = useLocation();
  const signUpEntry = (location.state as SignUpLocationState | null) ?? null;
  const skipPhoneIntro = Boolean(signUpEntry?.skipPhoneIntro);
  const prefilledPhone = signUpEntry?.prefilledPhone ?? "";
  const { validateResaleCode } = useResaleCode();
  const { value: smsTimerValue, restart: restartTimer } = useCountdown();

  const [isOpenPhoneNumberDialog, setIsOpenPhoneNumberDialog] =
    useState(!skipPhoneIntro);
  const [isOpenAlreadyRegistered, setIsOpenAlreadyRegistered] = useState(false);
  const [isOpenChooseValidation, setIsOpenChooseValidation] = useState(false);
  const [isOpenSignUp, setIsOpenSignUp] = useState(false);
  const [isOpenBlacklistedModal, setIsOpenBlacklistedModal] = useState(false);
  const [isOpenFinishSignUp, setIsOpenFinishSignUp] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [isInvalidCode, setIsInvalidCode] = useState(false);
  const [isFinishingSignUp, setIsFinishingSignUp] = useState(false);
  const [signupUserId, setSignupUserId] = useState<number>();

  const [email, setEmail] = useState("");
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(prefilledPhone);
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState<Gender>();
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [confirmWorker, setConfirmWorker] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [resaleCode, setResaleCode] = useState("");
  /** Step 2 (resale): user must choose before continuing; URL with /sign-up/:code preselects "yes" + field */
  const [resaleCodeAnswer, setResaleCodeAnswer] =
    useState<ResaleCodeAnswer>(null);
  const [otp, setOtp] = useState("");
  const [isVisibleResaleCodeTooltip, setIsVisibleResaleCodeTooltip] =
    useState(false);

  const [currentStep, setCurrentStep] = useState(1);

  const [validationType, setValidationType] = useState<"sms" | "whatsapp">();
  /** Dialog loading: global Loader Backdrop is below MUI Dialog z-index. */
  const [isSendingPhoneVerification, setIsSendingPhoneVerification] =
    useState(false);

  const rawPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";

    return "+55" + phoneNumber.replace(/[^\d]/g, "");
  }, [phoneNumber]);

  const phoneNumberError = useMemo(() => {
    if (rawPhoneNumber?.length === 0) return "";

    const isValid = fullPhoneNumberRegex.test(rawPhoneNumber);

    if (!isValid) {
      return "Número de telefone inválido.";
    }

    return "";
  }, [rawPhoneNumber]);

  const emailError = useMemo(() => {
    if (email.length === 0) return "";

    if (!isValidEmail(email)) {
      return "Formato de email inválido";
    }

    return "";
  }, [email]);

  const emailConfirmationError = useMemo(() => {
    if (emailConfirmation.length === 0) return "";

    if (!isValidEmail(emailConfirmation)) {
      return "Formato de email inválido";
    }

    if (email !== emailConfirmation) {
      return "Os e-mails devem ser iguais.";
    }

    return "";
  }, [email, emailConfirmation]);

  const nameError = useMemo(() => {
    if (!name) return "";

    const isValid = fullNameRegex.test(name);

    if (!isValid) {
      return "Nome incompleto.";
    }

    return "";
  }, [name]);

  const rawBirthdate = useMemo(() => {
    if (!birthDate) return "";

    const [day, month, year] = birthDate.split("/");

    return `${year}-${month}-${day}`;
  }, [birthDate]);

  const birthDateError = useMemo(() => {
    if (rawBirthdate?.length === 0) return "";

    const year = rawBirthdate.split("-").shift();
    if (
      !isValidDate(rawBirthdate) ||
      isAfter(rawBirthdate, new Date()) ||
      Number(year) < 1901
    ) {
      return "Data inválida.";
    }

    return "";
  }, [rawBirthdate]);

  const isValid = useMemo(() => {
    return (
      !!email &&
      !!name &&
      !nameError &&
      !!rawPhoneNumber &&
      !phoneNumberError &&
      !emailError &&
      !emailConfirmationError &&
      !!rawBirthdate &&
      !birthDateError &&
      !!gender &&
      !!selectedState &&
      !!selectedCity &&
      confirmWorker &&
      confirmTerms
    );
  }, [
    email,
    name,
    nameError,
    phoneNumberError,
    confirmWorker,
    confirmTerms,
    emailError,
    emailConfirmationError,
    rawPhoneNumber,
    rawBirthdate,
    birthDateError,
    gender,
    selectedState,
    selectedCity,
  ]);

  /** OTP UI: step 2 without resale, step 3 with resale, or unfinished signup on step 2 + finishing flag */
  const isOtpValidationStep = useMemo(() => {
    if (!realityInfo.showResaleCode && currentStep === 2) return true;
    if (realityInfo.showResaleCode && currentStep === 3) return true;
    if (realityInfo.showResaleCode && currentStep === 2 && isFinishingSignUp) return true;
    return false;
  }, [currentStep, isFinishingSignUp]);

  const buttonText = useMemo(() => {
    if (isOtpValidationStep) {
      return "Validar código";
    }
    return "Confirmar inscrição";
  }, [isOtpValidationStep]);

  const isResaleCodeFormatValid = useMemo(() => {
    const c = resaleCode.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return /^[A-Z]\d{5}$/.test(c);
  }, [resaleCode]);

  /** Valid /sign-up/:code in URL — skip step 2 (resale question + field) and validate after step 1 */
  const hasResaleCodeFromUrl = useMemo(() => {
    if (resaleCodeFromRoute == null || String(resaleCodeFromRoute).trim() === "") {
      return false;
    }
    const normalized = String(resaleCodeFromRoute).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return /^[A-Z]\d{5}$/.test(normalized);
  }, [resaleCodeFromRoute]);

  const canContinueResaleStep = useMemo(() => {
    if (!realityInfo.showResaleCode) return true;
    if (currentStep !== 2 || isFinishingSignUp) return true;
    if (resaleCodeAnswer === null) return false;
    if (resaleCodeAnswer === "no") return true;
    return isResaleCodeFormatValid;
  }, [
    currentStep,
    isFinishingSignUp,
    resaleCodeAnswer,
    isResaleCodeFormatValid,
  ]);

  const goBack = () => {
    window.history.back();
  };

  const goToSignIn = () => {
    navigate(`/sign-in`, {
      state: { prefilledPhone: phoneNumber } satisfies SignInLocationState,
    });
  };

  const goToWebsite = () => {
    window.location.href = realityInfo.websiteUrl!;
  };

  const onValidatePhone = async () => {
    const isValidPhone =
      phoneNumberError.length === 0 && rawPhoneNumber.length > 0;
    if (!isValidPhone) return;

    try {
      setIsLoadingButton(true);
      const blacklist = await getBlacklist();

      if (blacklist.phones.includes(rawPhoneNumber)) {
        return setIsOpenBlacklistedModal(true);
      }

      const { isAvailable, isPhoneNumberValidated, userId } =
        await getPhoneNumberAvailability(rawPhoneNumber);
      setIsOpenPhoneNumberDialog(false);
      setSignupUserId(userId ?? undefined);

      if (!isAvailable) {
        await setReseleCodeAvailability(userId, resaleCode);
      }
      if (isAvailable) {
        return setIsOpenSignUp(true);
      } else if (!isPhoneNumberValidated) {
        setIsFinishingSignUp(true);
        return setIsOpenFinishSignUp(true);
      }

      setIsOpenAlreadyRegistered(true);
    } catch (err) {
      console.error("Error validating phone", err);
      toast("Erro validando telefone", { type: "error" });
    } finally {
      setIsLoadingButton(false);
    }
  };

  const closeSignupDialog = () => {
    setIsOpenSignUp(false);
  };

  /**
   * Allows user to fix phone number before requesting OTP.
   * This only changes the local form state; backend updates depend on subsequent flow.
   */
  const onFixPhoneNumber = () => {
    setIsOpenChooseValidation(false);
    setIsOpenFinishSignUp(false);
    setCurrentStep(1);
    setIsOpenPhoneNumberDialog(true);
  };

  const toggleIsVisibleResaleCodeTooltip = () => {
    setIsVisibleResaleCodeTooltip((old) => !old);
  };

  const verifyEmailAvailability = async () => {
    try {
      loader.show("verify-email");
      const { isAvailable } = await getEmailAvailability(email);

      if (!isAvailable) {
        toast("E-mail já cadastrado", { type: "error" });
        return false;
      }

      return true;
    } catch (err) {
      toast("Erro verificando e-mail", { type: "error" });
      console.error("Erro verificando e-mail");
    } finally {
      loader.hide("verify-email");
    }
  };

  const createAccount = async (finalResaleCode?: string) => {
    try {
      loader.show("sign_up");
      const response = await signUpLite({
        email,
        name,
        phoneNumber: rawPhoneNumber,
        birthDate: rawBirthdate,
        gender: gender!,
        state: selectedState,
        city: selectedCity,
        resaleCode: finalResaleCode,
      });
      const newUserId = response?.user?.id;
      if (newUserId == null) {
        toast("Resposta inválida do servidor. Tente novamente.", {
          type: "error",
        });
        return false;
      }
      setSignupUserId(newUserId);

      return true;
    } catch (err) {
      toast("Erro ao criar conta", { type: "error" });
      console.error("Erro ao criar conta", err);
      return false;
    } finally {
      loader.hide("sign_up");
    }
  };

  const validateOtpCode = async () => {
    if (validationType === "sms") {
      setIsInvalidCode(false);

      try {
        loader.show("verify_code");

        await window.confirmationResult?.confirm(otp);

        // Fix 9: Verificar se claims existem, se não, chamar backend para setar
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken(true);
          // Decodificar payload do JWT para verificar claims
          const payload = JSON.parse(atob(token.split('.')[1]));

          if (!payload.appUserId) {
            await firebaseSignup(rawPhoneNumber, signupUserId);
            await user.getIdToken(true);
          }
        }

      } catch (error) {
        toast("Código inválido ou inexistente", { type: "error" });
        console.error("Código inválido ou inexistente");
        resetReCaptcha();

        return false;
      } finally {
        loader.hide("verify_code");
      }
    } else {
      try {
        loader.show("verify_code");
        const { isExpired, isValid, accessToken } = await validateWhatsappCode(
          rawPhoneNumber,
          otp
        );

        if (isExpired) {
          toast("Código expirado!", { type: "error" });
          return false;
        }

        if (!isValid) {
          toast("Código inválido ou inexistente", { type: "error" });
          return false;
        }

        await signInWithCustomToken(auth, accessToken);
        // Forçar refresh do ID token para obter claims atualizados
        await auth.currentUser?.getIdToken(true);
        return true;
      } catch (err) {
        toast("Erro ao validar o código", { type: "error" });
        console.error("Erro ao validar o código", err);
      } finally {
        loader.hide("verify_code");
      }
    }

    return true;
  };

  const onPressButton = async (withResaleCode = false) => {
    if (currentStep === 1) {
      if (!isValid) {
        return toast("Preencha todos os campos corretamente", {
          type: "error",
        });
      }

      const isAvailableEmail = await verifyEmailAvailability();
      if (!isAvailableEmail) return;

      const blacklist = await getBlacklist();

      if (blacklist.emails.includes(email.toLocaleLowerCase())) {
        return setIsOpenBlacklistedModal(true);
      }

      if (realityInfo.showResaleCode) {
        if (hasResaleCodeFromUrl) {
          if (!isResaleCodeFormatValid) {
            return toast("Código de revenda na URL inválido. Use o formato: uma letra e 5 números.", {
              type: "error",
            });
          }
          const codeOk = await validateResaleCode(resaleCode);
          if (!codeOk) {
            return toast(
              "O código da revenda inserido está incorreto. Verifique e tente novamente.",
              { type: "error" }
            );
          }
        }
        return setIsOpenChooseValidation(true);
      }

      return setIsOpenChooseValidation(true);
    }

    if (currentStep === 2 && withResaleCode && !isFinishingSignUp) {
      if (!resaleCode) {
        return toast(
          "Para seguir com o código, é necessário preencher o campo do código da revenda.",
          { type: "error" }
        );
      }

      const isValid = await validateResaleCode(resaleCode);
      if (!isValid) {
        return toast(
          "O código da revenda inserido está incorreto. Verifique e tente novamente.",
          { type: "error" }
        );
      }

      const accountCreated = await createAccount(resaleCode);
      if (!accountCreated) return;
      return setIsOpenChooseValidation(true);
    }

    if (
      currentStep === 2 &&
      realityInfo.showResaleCode &&
      !withResaleCode &&
      !isFinishingSignUp
    ) {
      const accountCreated = await createAccount();
      if (!accountCreated) return;
      return setIsOpenChooseValidation(true);
    }

    if (otp.length < 6) {
      return setIsInvalidCode(true);
    }

    const valid = await validateOtpCode();
    if (!valid) return setIsInvalidCode(true);

    resetTokenRefresh();

    setPendingPhoneValidatedSyncFlag();
    try {
      await updateUserInfo({ phoneNumberValidated: true });
      clearPendingPhoneValidatedSyncFlag();
    } catch (err) {
      console.error("Erro ao salvar verificação do celular", err);
      toast(
        "Não foi possível salvar a verificação do celular. Ao navegar, tentaremos novamente.",
        { type: "warning", toastId: "phone-validated-sync-failed" }
      );
      return;
    }

    const uid = auth.currentUser?.uid;
    if (uid) {
      const key = ["user-info", true, uid] as const;
      const prev = queryClient.getQueryData<UserInfo>(key);
      if (prev) {
        queryClient.setQueryData<UserInfo>(key, { ...prev, phoneNumberValidated: true });
      } else {
        await queryClient.refetchQueries({ queryKey: ["user-info"], exact: false });
      }
    } else {
      await queryClient.refetchQueries({ queryKey: ["user-info"], exact: false });
    }
    // toast(
    //   "Informe CPF, principal atividade de obra e tempo de profissão para concluir seu cadastro.",
    //   { type: "info", toastId: "post-otp-complete-profile" }
    // );
    navigate("/dashboard", { replace: true });
  };

  const submitPrimaryStep = () => {
    if (
      currentStep === 2 &&
      realityInfo.showResaleCode &&
      !isFinishingSignUp
    ) {
      return onPressButton(resaleCodeAnswer === "yes");
    }
    return onPressButton();
  };

  const ensureAccountCreatedForValidation = async () => {
    if (isFinishingSignUp || signupUserId) return true;

    const finalResaleCode =
      realityInfo.showResaleCode && hasResaleCodeFromUrl ? resaleCode : undefined;

    return createAccount(finalResaleCode);
  };

  const createFirebaseUser = async () => {
    if (!!phoneNumberError || !rawPhoneNumber) {
      return;
    }

    try {
      setIsOpenFinishSignUp(false);
      loader.show("firebase_signup");
      await firebaseSignup(rawPhoneNumber, signupUserId);
    } catch (error) {
      console.error("Erro ao criar conta no firebase", error);
      toast("Erro ao preparar autenticação. Tente novamente.", { type: "error" });
      throw error; // Fix 8: Relançar erro para parar o fluxo
    } finally {
      loader.hide("firebase_signup");
    }
  };

  const onSendSmsCode = async () => {
    setValidationType("sms");
    setIsSendingPhoneVerification(true);
    try {
      const accountCreated = await ensureAccountCreatedForValidation();
      if (!accountCreated) {
        setIsSendingPhoneVerification(false);
        return;
      }

      await createFirebaseUser();
      await new Promise((r) => setTimeout(r, MS_AFTER_FIREBASE_SIGNUP_BEFORE_SMS));
      const smsSent = await sendSignInSmsCode();
      if (!smsSent) {
        navigate("/sign-in", { replace: true });
      }
    } catch {
      navigate("/sign-in", { replace: true });
      setIsSendingPhoneVerification(false);
    }
  };

  const onSendWhatsappCode = async (disableNextStep = false) => {
    setValidationType("whatsapp");
    setIsSendingPhoneVerification(true);
    try {
      await createFirebaseUser();
      loader.show("send_whatsapp_code");
      await sendWhatsappCode(rawPhoneNumber);
      if (!disableNextStep) {
        setCurrentStep((prev) => {
          if (prev === 2) return 3;
          if (prev === 1 && realityInfo.showResaleCode) return 3;
          return prev;
        });
      }
    } catch (err) {
      console.error("Erro ao enviar código", err);
      toast("Erro ao enviar código", { type: "error" });
    } finally {
      setIsOpenChooseValidation(false);
      loader.hide("send_whatsapp_code");
      setIsSendingPhoneVerification(false);
    }
  };

  const sendSignInSmsCode = async (disableNextStep = false) => {
    setIsSendingPhoneVerification(true);
    loader.show("sign_in");

    if (!rawPhoneNumber.length) {
      loader.hide("sign_in");
      setIsSendingPhoneVerification(false);
      return false;
    }

    const sendSmsOnce = (verifier: RecaptchaVerifier) =>
      signInWithPhoneNumber(auth, rawPhoneNumber, verifier);

    const isFatalPhoneAuthError = (err: unknown) => {
      const code = err instanceof FirebaseError ? err.code : "";
      return (
        code === "auth/invalid-phone-number" ||
        code === "auth/missing-phone-number" ||
        code === "auth/operation-not-allowed" ||
        code === "auth/quota-exceeded" ||
        code === "auth/too-many-requests"
      );
    };

    const isTransientPhoneAuthError = (err: unknown) => {
      const code = err instanceof FirebaseError ? err.code : "";
      return (
        code === "auth/internal-error" ||
        code === "auth/network-request-failed" ||
        code === "auth/unknown" ||
        !code
      );
    };

    try {
      // Invisible reCAPTCHA must be rendered once before signInWithPhoneNumber (was inverted before).
      if (window.recaptchaVerifier && window.recaptchaWidgetId === undefined) {
        const widgetId = await window.recaptchaVerifier.render();
        window.recaptchaWidgetId = widgetId;
      }

      const verifier = window.recaptchaVerifier!;

      let confirmationResult: ConfirmationResult | undefined;
      let lastError: unknown;

      // One user action → up to N attempts only when Firebase says transient (e.g. network).
      // reCAPTCHA: one signInWithPhoneNumber call; Google may show the challenge several times
      // inside that call — if it still fails, we stop (no loop) so we don't spam Identity Toolkit.
      for (let attempt = 1; attempt <= MAX_SMS_TRANSIENT_RETRIES; attempt++) {
        try {
          if (attempt > 1) {
            await new Promise((r) => setTimeout(r, 500 * (attempt - 1)));
          }
          confirmationResult = await sendSmsOnce(verifier);
          break;
        } catch (err) {
          lastError = err;
          if (isFatalPhoneAuthError(err)) {
            throw err;
          }
          if (isFirebasePhoneAuthCaptchaError(err)) {
            resetReCaptcha();
            throw err;
          }
          if (!isTransientPhoneAuthError(err)) {
            throw err;
          }
          if (attempt >= MAX_SMS_TRANSIENT_RETRIES) {
            throw lastError;
          }
        }
      }

      if (!confirmationResult) {
        throw lastError ?? new Error("SMS send failed");
      }

      window.confirmationResult = confirmationResult;
      // Only move 2 → 3 (OTP step). A second call would advance to 4 and hide the OTP UI
      // (see RecaptchaVerifier callback: must not call sendSignInSmsCode again).
      if (!disableNextStep) {
        setCurrentStep((prev) => {
          if (prev === 2) return 3;
          if (prev === 1 && realityInfo.showResaleCode) return 3;
          return prev;
        });
      }
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[sendSignInSmsCode]", error);
      }
      toast(getFirebasePhoneAuthToastMessage(error), {
        type: "error",
        toastId: "phone-auth-send-error",
      });
      resetReCaptcha();
      return false;
    } finally {
      loader.hide("sign_in");
      setIsSendingPhoneVerification(false);
      setIsOpenChooseValidation(false);
    }
  };

  const resendCode = () => {
    restartTimer();
    if (validationType === "sms") {
      sendSignInSmsCode(true);
    } else {
      onSendWhatsappCode(true);
    }
  };

  const resetReCaptcha = () => {
    if (
      typeof grecaptcha !== "undefined" &&
      typeof window.recaptchaWidgetId !== "undefined"
    ) {
      grecaptcha.reset(window.recaptchaWidgetId);
    }
  };

  useEffect(() => {
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        // Do not call sendSignInSmsCode here: invisible reCAPTCHA invokes this when the
        // challenge succeeds, which already happens inside signInWithPhoneNumber. A second
        // call duplicated SMS send and nextStep(), moving the stepper past the OTP screen.
        callback: () => undefined,
      }
    );
    return () => {
      try {
        window.recaptchaVerifier?.clear();
      } catch {
        /* ignore */
      }
      window.recaptchaVerifier = undefined;
      window.recaptchaWidgetId = undefined;
    };
  }, []);

  useEffect(() => {
    resetReCaptcha();
  }, [rawPhoneNumber]);

  useEffect(() => {
    restartTimer();
  }, [currentStep]);

  useEffect(() => {
    if (resaleCodeFromRoute != null && String(resaleCodeFromRoute).trim() !== "") {
      setResaleCode(String(resaleCodeFromRoute).toUpperCase());
      setResaleCodeAnswer("yes");
    }
  }, [resaleCodeFromRoute]);

  return {
    /** True when user came from login (no account) — intro dialog skipped; phone may be prefilled. */
    skipPhoneIntro,
    isOpenPhoneNumberDialog,
    setIsOpenPhoneNumberDialog,
    isOpenAlreadyRegistered,
    isOpenChooseValidation,
    setIsOpenChooseValidation,
    isOpenSignUp,
    isOpenBlacklistedModal,
    isOpenFinishSignUp,
    onSendSmsCode,
    onSendWhatsappCode,
    goBack,
    goToWebsite,
    goToSignIn,
    onValidatePhone,
    closeSignupDialog,
    onFixPhoneNumber,
    isLoadingButton,
    currentStep,
    setCurrentStep,

    email,
    setEmail,
    emailError,
    emailConfirmation,
    setEmailConfirmation,
    emailConfirmationError,
    name,
    setName,
    nameError,
    phoneNumber,
    setPhoneNumber,
    phoneNumberError,
    otp,
    setOtp,
    isInvalidCode,
    isFinishingSignUp,
    smsTimerValue,

    birthDate,
    setBirthDate,
    birthDateError,
    gender,
    setGender,
    selectedState,
    setSelectedState,
    selectedCity,
    setSelectedCity,

    confirmWorker,
    setConfirmWorker,
    confirmTerms,
    setConfirmTerms,

    resaleCode,
    setResaleCode,
    resaleCodeAnswer,
    setResaleCodeAnswer,
    isVisibleResaleCodeTooltip,
    toggleIsVisibleResaleCodeTooltip,

    buttonText,
    onPressButton,
    submitPrimaryStep,
    canContinueResaleStep,
    sendSignInSmsCode,
    isValid,
    resendCode,

    isSendingPhoneVerification,

    hasResaleCodeFromUrl,
  };
};
